import React, { Fragment, useEffect, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { getUnifiedAIExperienceName } from '../../../common/utils/get-unified-ai-experience-name';
import { logger } from '../../../common/utils/logger';
import {
	AiBrand,
	ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK,
	ATLASSIAN_AI_TROUBLESHOOT_LINK,
	ATLASSIAN_AI_USAGE_LIMITS_LINK,
	ATLASSIAN_HIPAA_COMPLICANCE_LINK,
	ATLASSIAN_INTELLIGENCE_SEARCH_INFO_LINK,
} from '../../../constants';
import { messages } from '../../../messages';
import { StreamingEarlyStopCard } from '../../../v2/common/ui/streaming-early-stop-card';
import { DefinitionError } from '../../../v2/definitions/body/error/definition-error';
import { useAIAnswerContext } from '../../ai-answer-context';
import { NLPSearchErrorState } from '../../types';

import { AIAnswerResultSection, InfoLink } from './styled';

export const AIAnswerError = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		advancedSearchUrl,
		commonAttributes,
		isReadingAids,
		brand,
		smartAnswersRoute,
		sourceProduct,
	} = useAIAnswerContext();
	const {
		errorState,
		errorCode,
		answerCardType,
		source,
		apiSource,
		searchSessionId,
		sessionId,
		queryHash,
	} = commonAttributes;
	const { trackAIResultError } = useAIEventsInstrumentation();

	useEffect(() => {
		if (errorState !== NLPSearchErrorState.Default) {
			trackAIResultError(
				{ aiErrorMessage: errorState || '', aiErrorCode: errorCode ?? 0 },
				{
					attributes: {
						aiExperienceName: getUnifiedAIExperienceName(smartAnswersRoute),
						aiFeatureName: isReadingAids
							? `Definitions ${sourceProduct ? `(${sourceProduct})` : ''}`
							: 'smartAnswers',
						aiInteractionId: isReadingAids ? undefined : searchSessionId,
						aiInteractionType: isReadingAids ? undefined : 'searchSessionId',
					},
				},
			);
			if (fg('kd_enable_networkerror_logging')) {
				logger.error({
					error: { errorState, errorCode, answerCardType, source, apiSource, sessionId, queryHash },
					functionName: 'AIAnswerError',
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const brandName = brand === AiBrand.ROVO ? 'Rovo' : 'Atlassian Intelligence';

	const errorMessage = useMemo(() => {
		switch (errorState) {
			case NLPSearchErrorState.NoAnswer:
			case NLPSearchErrorState.NoAnswerSearchResults:
			case NLPSearchErrorState.NoAnswerHydration:
			case NLPSearchErrorState.NoAnswerRelevantContent:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_no_answer}
						values={{
							brandName,
							learnMoreLink: (
								<InfoLink href={ATLASSIAN_INTELLIGENCE_SEARCH_INFO_LINK} target={'_blank'}>
									{formatMessage(messages.ai_answer_onboarding_banner_tooltip_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.NoAnswerKeywords:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_keyword}
						values={{
							brandName,
							learnMoreLink: (
								<InfoLink href={ATLASSIAN_INTELLIGENCE_SEARCH_INFO_LINK} target={'_blank'}>
									{formatMessage(messages.ai_answer_onboarding_banner_tooltip_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.AcceptableUseViolation:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_acceptable_use}
						values={{
							brandName,
							acceptableUseLink: (
								<InfoLink href={ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK} target={'_blank'}>
									{formatMessage(messages.ai_answer_component_error_state_acceptable_use_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.SubjectiveQuery:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_subjective_query}
						values={{ brandName }}
					/>
				);
			case NLPSearchErrorState.NoAnswerOpenAIRateLimitUserAbuse:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_open_ai_rate_limit_error}
						values={{
							brandName,
							supportLink: (
								<InfoLink href={ATLASSIAN_AI_USAGE_LIMITS_LINK} target={'_blank'}>
									{formatMessage(messages.ai_answer_component_support_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.NoAnswerWhoQuestion:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_no_who_is_answer}
						values={{ brandName }}
					/>
				);
			case NLPSearchErrorState.NetworkError:
			case NLPSearchErrorState.NoAnswerOpenAIResponseError:
			case NLPSearchErrorState.AIDisabled:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_network}
						values={{
							brandName,
							supportLink: (
								<InfoLink href={ATLASSIAN_AI_TROUBLESHOOT_LINK} target={'_blank'}>
									{formatMessage(messages.ai_answer_component_support_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.HIPAAError:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_error_state_hipaa}
						values={{
							supportLink: (
								<InfoLink href={ATLASSIAN_HIPAA_COMPLICANCE_LINK} target={'_blank'}>
									{formatMessage(messages.ai_answer_component_hipaa_compliance_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.EarlyStop:
				return <StreamingEarlyStopCard />;
			case NLPSearchErrorState.Default:
			default:
				return (
					<FormattedMessage
						{...messages.ai_answer_component_result_error_text}
						values={{
							brandName,
							advancedSearchLink: !!advancedSearchUrl ? (
								fg('dst-a11y__replace-anchor-with-link__knowledge-disc') ? (
									<Link href={advancedSearchUrl}>
										{formatMessage(messages.ai_answer_component_result_error_advanced_search_link)}
									</Link>
								) : (
									// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
									<a href={advancedSearchUrl}>
										{formatMessage(messages.ai_answer_component_result_error_advanced_search_link)}
									</a>
								)
							) : (
								formatMessage(messages.ai_answer_component_result_error_advanced_search_link)
							),
						}}
					/>
				);
		}
	}, [advancedSearchUrl, errorState, formatMessage, brandName]);

	return (
		<Box testId="ai-answer-error-container">
			{isReadingAids ? (
				<DefinitionError />
			) : (
				<Fragment>
					<AIAnswerResultSection isReadingAids={isReadingAids} isExpanded>
						{errorMessage}
					</AIAnswerResultSection>
				</Fragment>
			)}
		</Box>
	);
};
